import {FC, useState, useEffect} from 'react'
import {useDispatch} from 'react-redux'
import {useForm} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import {useHistory} from 'react-router-dom'
import * as yup from 'yup'
import {keys, pickBy} from 'lodash'
import {reduce} from 'lodash/fp'
import classNames from 'classnames'
import Button from 'jbc-front/components/Button'
import {Modal} from 'jbc-front/components/presenters/ui/Modal'
import {ButtonRow} from 'jbc-front/components/presenters/layout/ButtonRow'
import {Required} from 'jbc-front/components/presenters/form/Label'
import {Spacer} from 'jbc-front/components/presenters/Spacer'
import {Radio, RadioContainer} from 'jbc-front/components/presenters/form/Radio'
import {Input} from 'jbc-front/components/presenters/form/Input'
import {Checkbox} from 'jbc-front/components/presenters/form/Checkbox'
import {ErrorMessage} from 'jbc-front/components/presenters/form/ErrorMessage'
import {notifySuccess} from 'store/actions/notify'
import {asyncError} from 'store/actions/asyncError'
import {useSelector} from 'hooks/redux'
import {useFetchProcedureCsvSerialNumbers} from 'hooks/api/exportProcedureCsv'
import {ProcedureKind} from 'types/api/csvProcedureStatuses/procedureKind'
import {ProcedureStatus} from 'types/api/procedureStatuses/procedureStatus'
import {useDownloadProcedureCsv, usePostProcedureCsvRequest} from 'hooks/api/exportProcedureCsv'
import styles from './CsvDownloadModal.scss'

// @ts-ignore
import {isLimitedAdminSelector} from 'utils'

const MAX_DOWNLOAD_COUNT = 400
type Item = {
  name: string
}

type CsvDownloadModalProps = {
  selected: {[key: number]: Item}
  procedureKind: ProcedureKind
  procedureStatuses: ProcedureStatus[]
  officeIds: string[]
}

const insuranceTypes = {
  social_insurance: '社会保険',
  employment_insurance: '雇用保険',
}

const insuranceTypeByProcedureKind = (procedureKind: ProcedureKind) => {
  switch (procedureKind) {
    case 'r1':
    case 'r4':
    case 'r5':
    case 'r6':
    case 'r7':
      return 'social_insurance'
    case 'r57':
    case 'r58':
    case 'r60':
    case 'r62':
      return 'employment_insurance'
  }
}

const schema = yup.object().shape({
  procedure_kind: yup.mixed<ProcedureKind>().required(),
  procedure_status_ids: yup.array(yup.string().required()).required(),
  serial_number: yup
    .string()
    .defined()
    .test(
      'isSerialNumber',
      '001〜999までの数字を入力してください。',
      (value) => !!value && value !== '000' && /^\d{3}$/.test(value)
    ),
  show_my_number: yup.bool().required(),
  submission_destination: yup
    .string()
    .oneOf(['pension_office', 'health_insurance_office'], '提出先は年金事務所か健康保険組合を選択してください')
    .when('$insuranceType', ([insuranceType], schema) =>
      insuranceType === 'social_insurance' ? schema.required() : schema.notRequired()
    ),
  office_ids: yup.array(yup.string().required()).required(),
})

type Schema = yup.InferType<typeof schema>

const makeDefaultValues = (procedureKind: ProcedureKind, insuranceType: string, officeIds: string[]): Schema => {
  const values = {
    procedure_kind: procedureKind,
    procedure_status_ids: [],
    serial_number: '',
    show_my_number: false,
    office_ids: officeIds,
  }
  if (insuranceType === 'social_insurance') {
    const submissionDestination = procedureKind === 'r5' ? 'health_insurance_office' : 'pension_office'
    return {...values, submission_destination: submissionDestination}
  } else {
    return values
  }
}

export const CsvDownloadModal: FC<CsvDownloadModalProps> = ({
  selected,
  procedureKind,
  procedureStatuses,
  officeIds,
}) => {
  const selectedCount = reduce((sum, value) => (value ? sum + 1 : sum), 0, selected)
  const downloadable = selectedCount > 0
  const procedureStatusIds = keys(pickBy(selected))
  const insuranceType = insuranceTypeByProcedureKind(procedureKind)
  const client = useSelector((state) => state.client.current)
  const isLimitedAdmin = useSelector(isLimitedAdminSelector)
  const showMyNumberForm = !isLimitedAdmin && procedureKind !== 'r62'
  const download = useDownloadProcedureCsv()
  const postCsvRequest = usePostProcedureCsvRequest()
  const dispatch = useDispatch()
  const history = useHistory()
  const [isOpen, setIsOpen] = useState(false)
  const healthInsuranceType = procedureStatuses[0]?.employee?.office?.health_insurance_type
  const isDisabledSubmissionDestination =
    (['r1', 'r7'].includes(procedureKind) && healthInsuranceType === 'kyokai') ||
    ['r4', 'r5', 'r6'].includes(procedureKind)
  const {resource: procedureCsvSerialNumbers, getter: serialNumbersGetter} = useFetchProcedureCsvSerialNumbers(
    client.id
  )

  const {
    handleSubmit: handleReactHookFormSubmit,
    register,
    setValue,
    watch,
    trigger,
    reset,
    formState: {errors, isSubmitting, isValid},
  } = useForm({
    resolver: yupResolver(schema),
    context: {insuranceType},
    mode: 'onChange',
    defaultValues: makeDefaultValues(procedureKind, insuranceType, officeIds),
  })
  const submissionDestination = watch('submission_destination')

  const handleClose = () => {
    reset()
    setIsOpen(false)
  }
  const handleSubmit = async (data: Schema) => {
    if (procedureStatusIds.length >= MAX_DOWNLOAD_COUNT) {
      try {
        await postCsvRequest({...data, procedure_status_ids: procedureStatusIds})
        dispatch(notifySuccess('手続きデータはメールにてお送りします'))
        history.push('/procedure_statuses')
      } catch (err) {
        dispatch(asyncError(err))
      }
    } else {
      await download({...data, procedure_status_ids: procedureStatusIds})
      serialNumbersGetter()
    }
    handleClose()
  }

  useEffect(() => {
    if (isOpen) {
      serialNumbersGetter()
    }
  }, [isOpen])

  useEffect(() => {
    const destinationType =
      insuranceType === 'social_insurance' ? submissionDestination || 'pension_office' : 'employment_insurance'
    if (procedureCsvSerialNumbers) {
      setValue('serial_number', procedureCsvSerialNumbers[destinationType])
      trigger('serial_number')
    }
  }, [procedureCsvSerialNumbers])

  useEffect(() => {
    if (submissionDestination && procedureCsvSerialNumbers) {
      setValue('serial_number', procedureCsvSerialNumbers[submissionDestination])
      trigger('serial_number')
    }
  }, [submissionDestination])

  return (
    <>
      <Button disabled={!downloadable} primary onClick={() => setIsOpen(true)}>
        ダウンロード
      </Button>

      <Modal isOpen={isOpen} onClose={handleClose} className={styles.csvModal}>
        <form onSubmit={handleReactHookFormSubmit(handleSubmit)}>
          <Modal.Header onClose={handleClose}>{`CSV(${insuranceTypes[insuranceType]})ダウンロード`}</Modal.Header>
          <Modal.Body>
            {insuranceType === 'social_insurance' && (
              <>
                <div className={styles.itemBox}>
                  <p>
                    提出先
                    <Required />
                  </p>
                  <div>
                    <RadioContainer>
                      <Radio
                        {...register('submission_destination')}
                        value="pension_office"
                        disabled={isDisabledSubmissionDestination}
                      >
                        年金事務所
                      </Radio>
                      <Radio
                        {...register('submission_destination')}
                        value="health_insurance_office"
                        disabled={isDisabledSubmissionDestination}
                      >
                        健康保険組合
                      </Radio>
                    </RadioContainer>
                    {errors.submission_destination?.message && (
                      <ErrorMessage>{errors.submission_destination.message}</ErrorMessage>
                    )}
                  </div>
                </div>
                <Spacer direction="y" size={30} />
              </>
            )}

            <div className={styles.itemBox}>
              <p>
                {insuranceType === 'social_insurance' ? '媒体通番' : 'FD通番'}
                <Required />
              </p>
              <div>
                <Input {...register('serial_number')} isError={!!errors.serial_number?.message} maxLength={3} />
                {errors.serial_number?.message && <ErrorMessage>{errors.serial_number.message}</ErrorMessage>}
              </div>
            </div>

            {showMyNumberForm && (
              <>
                <Spacer direction="y" size={30} />
                <div className={classNames(styles.itemBox, styles.checkbox)}>
                  <Checkbox {...register('show_my_number')}>マイナンバーを出力する</Checkbox>
                </div>
                <div>
                  {errors.show_my_number?.message && <ErrorMessage>{errors.show_my_number.message}</ErrorMessage>}
                </div>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <ButtonRow>
              <Button onClick={handleClose}>キャンセル</Button>
              <Button primary disabled={isSubmitting || !isValid} onClick={handleReactHookFormSubmit(handleSubmit)}>
                ダウンロード
              </Button>
            </ButtonRow>
          </Modal.Footer>
        </form>
      </Modal>
    </>
  )
}
